import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';

export interface ReactAdapterProviderProps<P = {}> {
  component: React.ComponentType<P> | string;
  children?: React.ReactNode;
  [key: string]: any;
}

class ReactAdapterProvider<P = {}> extends React.Component<ReactAdapterProviderProps<P>> {
  private refHold: HTMLDivElement | null = null;
  private root: any = null;

  init = (hydrate?: boolean) => {
    const { component, children, ...rest } = this.props;

    if (this.refHold) {
      if (!this.root) {
        // Create the root only once
        this.root = hydrate
          ? hydrateRoot(this.refHold, React.createElement(component as React.ComponentType<any>, rest as any, children))
          : createRoot(this.refHold);
      }

      // Use the existing root to render
      this.root.render(
        React.createElement(component as React.ComponentType<any>, rest as any, children)
      );
    }
  };

  componentDidUpdate() {
    this.init(true);
  }

  componentDidMount() {
    this.init();
  }

  componentWillUnmount() {
    if (this.root) {
      // Defer the unmount so it never runs synchronously while the host tree
      // is still rendering/committing. Unmounting a nested root during the
      // parent's render triggers React's "synchronously unmount a root while
      // React was already rendering" warning and a potential race condition.
      const root = this.root;
      this.root = null;
      queueMicrotask(() => root.unmount());
    }
  }

  render() {
    return <div ref={(ref) => { this.refHold = ref; }} />;
  }
}

export default ReactAdapterProvider;